<template>
    <div
        class="editor"
        v-if="editor"
    >
        <rte-menu
            :editor="editor"
            class="editor__header"
        />
        <editor-content
            :editor="editor"
            class="rte"
        />
        <div class="editor__footer"></div>
    </div>
</template>

<script lang="ts" setup>
import { PluginKey } from 'prosemirror-state'
import tippy from 'tippy.js'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import MentionList from '@/components/MentionList.vue'
import RteMenu from '@/components/RteMenu.vue'
import { LesdoelCustom } from '@/helpers/lesdoel'
import { MentionCustom } from '@/helpers/mention'
import { TagCustom } from '@/helpers/tag'
import { IGroupedMentionUsers, INoteWithLabel, ITagVM } from '@/interfaces/INote'
import { LesdoelenService } from '@/services/lesdoelenService'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, VueRenderer, useEditor } from '@tiptap/vue-3'
import GroupedMentionList from '../GroupedMentionList.vue'

const Tagging = TagCustom

const Mentioning = MentionCustom

const Lesdoelen = LesdoelCustom

const searchTimeout = ref<NodeJS.Timeout | null>(null)
const debouncedLesdoelenSearch = (query: string, delay = 300): Promise<any[]> => {
    return new Promise((resolve, reject) => {
        if (searchTimeout.value) {
            clearTimeout(searchTimeout.value)
        }

        searchTimeout.value = setTimeout(async () => {
            try {
                const results = await LesdoelenService.searchLesdoelen(query)
                resolve(results)
            } catch (error) {
                reject(error)
            }
        }, delay)
    })
}

onBeforeUnmount(() => {
    if (searchTimeout.value) {
        clearTimeout(searchTimeout.value)
    }
})

const props = defineProps<{
    placeholder?: string
    tags?: ITagVM[]
    mentions?: IGroupedMentionUsers[]
    note?: INoteWithLabel
    disabled?: boolean
}>()

const modelValue = defineModel({ default: '' })

const editor = useEditor({
    extensions: [
        StarterKit.configure({
            heading: false,
        }),
        Link.configure({
            openOnClick: false,
        }),
        Highlight,
        Underline,
        TextStyle,
        Placeholder.configure({
            placeholder: props.placeholder,
        }),
        Tagging.configure({
            HTMLAttributes: {
                class: 'tag',
            },
            suggestion: {
                char: '#',
                pluginKey: new PluginKey('tag'),
                items: ({ query }) => {
                    const tags = props.tags?.filter((item) => item.type === '#' && item.name.toLowerCase().startsWith(query.toLowerCase())) ?? []
                    const tagsUnique = tags.filter((item, index) => tags.findIndex((tag) => tag.id === item.id) === index)
                    const tagsSlice = tagsUnique.slice(0, 15)
                    const potentialMatch = props.tags?.find((item) => item.type === '#' && item.name.toLowerCase() === query.toLowerCase())
                    const customtag = {
                        id: potentialMatch?.id || -1,
                        name: query,
                        type: '#',
                    }
                    return [...tagsSlice, customtag]
                },
                render: () => {
                    let component: any
                    let popup: any

                    return {
                        onStart: (props: any) => {
                            component = new VueRenderer(MentionList, {
                                props: props,
                                editor: editor.value!,
                            })

                            popup = tippy('body', {
                                getReferenceClientRect: props.clientRect,
                                appendTo: () => document.body,
                                content: component.element,
                                showOnCreate: true,
                                interactive: true,
                                trigger: 'manual',
                                placement: 'bottom-start',
                                theme: 'light',
                                arrow: false,
                            })
                        },
                        onUpdate(props) {
                            component.updateProps(props)

                            popup[0].setProps({
                                getReferenceClientRect: props.clientRect,
                            })
                        },
                        onKeyDown(props) {
                            return component.ref?.onKeyDown(props)
                        },
                        onExit() {
                            popup[0].destroy()
                            component.destroy()
                        },
                    }
                },
            },
        }),
        Mentioning.configure({
            HTMLAttributes: {
                class: 'mention',
            },
            suggestion: {
                char: '@',
                pluginKey: new PluginKey('mention'),
                items: ({ query }) => {
                    if (!query) return props.mentions ?? []
                    return (
                        props.mentions?.map((group: IGroupedMentionUsers) => {
                            return {
                                ...group,
                                gebruikers: group.gebruikers.filter((user) => {
                                    const name = `${user.voornaam} ${user.tussenvoegsel ?? ''} ${user.achternaam}`.replace(/\s+/g, ' ').trim()
                                    return name.toLowerCase().startsWith(query.toLowerCase())
                                }),
                            }
                        }) ?? []
                    )
                },
                render: () => {
                    let component: any
                    let popup: any

                    return {
                        onStart: (props: any) => {
                            component = new VueRenderer(GroupedMentionList, {
                                props: props,
                                editor: editor.value!,
                            })

                            popup = tippy('body', {
                                getReferenceClientRect: props.clientRect,
                                appendTo: () => document.body,
                                content: component.element,
                                showOnCreate: true,
                                interactive: true,
                                trigger: 'manual',
                                placement: 'bottom-start',
                                theme: 'light',
                                arrow: false,
                            })
                        },
                        onUpdate(props) {
                            component.updateProps(props)

                            popup[0].setProps({
                                getReferenceClientRect: props.clientRect,
                            })
                        },
                        onKeyDown(props) {
                            return component.ref?.onKeyDown(props)
                        },
                        onExit() {
                            popup[0].destroy()
                            component.destroy()
                        },
                    }
                },
            },
        }),
        // TODO: release with next iteration of curriculum data
        // Lesdoelen.configure({
        //     HTMLAttributes: {
        //         class: 'lesdoel',
        //     },
        //     suggestion: {
        //         char: '&',
        //         pluginKey: new PluginKey('lesdoel'),
        //         items: async ({ query }) => {
        //             const searchResults = await debouncedLesdoelenSearch(query)
        //             const lesdoelenMapped = searchResults.map((lesdoel) => ({
        //                 id: lesdoel.id,
        //                 name: lesdoel.naam,
        //                 type: '&' as const,
        //             }))
        //             const lesdoelenSlice = lesdoelenMapped.slice(0, 15)
        //             const potentialMatch = searchResults.find((item) => item.naam.toLowerCase() === query.toLowerCase())
        //             const customLesdoel = {
        //                 id: potentialMatch?.id || -1,
        //                 name: query,
        //                 type: '&' as const,
        //             }
        //             return [...lesdoelenSlice, customLesdoel]
        //         },
        //         render: () => {
        //             let component: any
        //             let popup: any

        //             return {
        //                 onStart: (props: any) => {
        //                     component = new VueRenderer(MentionList, {
        //                         props: props,
        //                         editor: editor.value!,
        //                     })

        //                     popup = tippy('body', {
        //                         getReferenceClientRect: props.clientRect,
        //                         appendTo: () => document.body,
        //                         content: component.element,
        //                         showOnCreate: true,
        //                         interactive: true,
        //                         trigger: 'manual',
        //                         placement: 'bottom-start',
        //                         theme: 'light',
        //                         arrow: false,
        //                     })
        //                 },
        //                 onUpdate(props) {
        //                     component.updateProps(props)

        //                     popup[0].setProps({
        //                         getReferenceClientRect: props.clientRect,
        //                     })
        //                 },
        //                 onKeyDown(props) {
        //                     return component.ref?.onKeyDown(props)
        //                 },
        //                 onExit() {
        //                     popup[0].destroy()
        //                     component.destroy()
        //                 },
        //             }
        //         },
        //     },
        // }),
    ],
    content: modelValue.value,
    editable: !props.disabled,
    onUpdate: () => {
        modelValue.value = editor.value?.getHTML() ?? ''
    },
})

onMounted(() => {
    watch(
        () => modelValue.value,
        (newVal) => {
            const isSame = editor.value?.getHTML() === newVal
            if (!isSame) {
                editor.value?.commands.setContent(newVal ?? '', false)
            }
        },
        {
            immediate: true,
        }
    )
})

onBeforeUnmount(() => {
    editor.value?.destroy()
})
</script>
<style lang="scss">
.tippy-content {
    padding: 0 !important;
}
</style>
